/**
 * Ingreso a almacén de una compra (Gestión → Compras).
 *
 * Puerto de la ventana «Detalles de Compra» del panel anterior
 * (`Compra::detalles_descarga` + `inventario/insertar_entrada` y
 * `inventario/delete_ingreso`): muestra las **líneas** de la compra con lo que ya
 * se ingresó y lo que falta, permite **registrar la entrada** (que mueve el stock
 * y marca la línea como almacenada) y **anular** las entradas ya registradas.
 *
 * La entrada se registra sobre el producto de la propia línea (el original daba
 * a elegir un producto por `nombre_item_api`); la anulación devuelve el stock y
 * deja la línea otra vez «por almacenar» si ya no le queda ninguna entrada.
 */

import { PackageCheck, PackagePlus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/http/api-error';

import {
  useAnularEntradaCompra,
  useEntradasDeCompra,
  useRegistrarEntradaCompra,
} from '../compras.api';
import type { EntradaDeCompra } from '../compras.types';
import { fechaCorta, hoyIso } from '../gestion-formato';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Cantidad con hasta tres decimales (o un guion). */
function cantidad(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return '—';

  return valor.toLocaleString('es-PE', { maximumFractionDigits: 3 });
}

interface Props {
  /** Compra abierta; `null` = cerrado. */
  compra: { id: number; comprobante: string } | null;
  onClose: () => void;
  /** Mensaje de éxito que muestra el listado. */
  onAviso: (mensaje: string) => void;
}

export function CompraAlmacenDialog({ compra, onClose, onAviso }: Props) {
  const compraId = compra?.id ?? null;
  const consulta = useEntradasDeCompra(compraId);
  const registrar = useRegistrarEntradaCompra(compraId);
  const anular = useAnularEntradaCompra();

  const datos = consulta.data;
  const productos = useMemo(() => datos?.productos ?? [], [datos]);
  const entradas = datos?.entradas ?? [];
  const pendientes = useMemo(() => productos.filter((item) => item.pendiente > 0), [productos]);

  const [productoId, setProductoId] = useState('');
  const [cantidadTexto, setCantidadTexto] = useState('');
  const [razon, setRazon] = useState('Ingreso a almacén');
  const [fecha, setFecha] = useState(hoyIso());
  const [error, setError] = useState<string | null>(null);
  const [porAnular, setPorAnular] = useState<EntradaDeCompra | null>(null);

  const seleccionada = productos.find((item) => item.producto_id === Number(productoId)) ?? null;

  // Al abrir otra compra se limpia el formulario.
  useEffect(() => {
    if (compraId === null) return;

    setProductoId('');
    setCantidadTexto('');
    setRazon('Ingreso a almacén');
    setFecha(hoyIso());
    setError(null);
  }, [compraId]);

  // Preselecciona la primera línea pendiente cuando llegan los datos.
  useEffect(() => {
    if (compraId === null || productoId) return;

    const primera = pendientes[0];

    if (!primera) return;

    setProductoId(String(primera.producto_id));
    setCantidadTexto(String(primera.pendiente));
  }, [compraId, pendientes, productoId]);

  const elegirProducto = (valor: string) => {
    const linea = productos.find((item) => item.producto_id === Number(valor));

    setProductoId(valor);
    setCantidadTexto(linea ? String(linea.pendiente) : '');
    setError(null);
  };

  const enviar = () => {
    setError(null);

    if (!seleccionada) {
      setError('Elige el producto a ingresar.');

      return;
    }

    const monto = Number(cantidadTexto);

    if (!Number.isFinite(monto) || monto <= 0) {
      setError('La cantidad a ingresar debe ser un número mayor que cero.');

      return;
    }

    if (monto > seleccionada.pendiente) {
      setError(
        `Lo pendiente por ingresar de ${seleccionada.producto} es ${cantidad(seleccionada.pendiente)}.`,
      );

      return;
    }

    registrar.mutate(
      {
        producto_id: seleccionada.producto_id,
        cantidad: monto,
        razon: razon.trim() || undefined,
        fecha_entrada: fecha || undefined,
      },
      {
        onSuccess: (respuesta) => {
          onAviso(respuesta.mensaje);
          setProductoId('');
          setCantidadTexto('');
        },
        onError: (fallo) =>
          setError(fallo instanceof ApiError ? fallo.message : 'No se pudo registrar la entrada.'),
      },
    );
  };

  const quitar = () => {
    if (!porAnular) return;

    setError(null);
    anular.mutate(porAnular.detalle_id, {
      onSuccess: () => {
        setPorAnular(null);
        onAviso('Entrada anulada. El stock volvió a su sitio.');
      },
      onError: (fallo) => {
        setPorAnular(null);
        setError(fallo instanceof ApiError ? fallo.message : 'No se pudo anular la entrada.');
      },
    });
  };

  return (
    <>
      <Dialog
        open={compra !== null}
        onClose={onClose}
        size="xl"
        title={`Ingreso a almacén ${compra?.comprobante ?? ''}`}
        description="Carga el stock de la compra: cada entrada mueve el inventario y marca la línea como almacenada."
        footer={
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cerrar
          </Button>
        }
      >
        {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

        {consulta.isError ? (
          <Alert variant="error" title="No se pudo abrir el ingreso a almacén">
            {consulta.error instanceof ApiError ? consulta.error.message : 'Inténtalo de nuevo.'}
          </Alert>
        ) : null}

        {error ? <Alert variant="error" title="No se pudo completar la acción">{error}</Alert> : null}

        {datos ? (
          <div className="space-y-5">
            {/* Registro de una entrada */}
            {pendientes.length > 0 ? (
              <section className="rounded-card border border-ink-100 bg-white p-4">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-800">
                  <PackagePlus className="size-4 text-brand-500" aria-hidden="true" />
                  Registrar entrada
                </h3>

                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <label className="flex flex-col gap-1.5 text-sm">
                    <span className="font-medium text-ink-700">Producto</span>
                    <Select
                      className={CONTROL}
                      value={productoId}
                      onChange={(evento) => elegirProducto(evento.target.value)}
                    >
                      {pendientes.map((linea) => (
                        <option key={linea.producto_id} value={linea.producto_id}>
                          {linea.producto} · pend. {cantidad(linea.pendiente)}
                        </option>
                      ))}
                    </Select>
                  </label>

                  <Input
                    label="Cantidad"
                    type="number"
                    step="0.001"
                    min={0}
                    value={cantidadTexto}
                    onChange={(evento) => setCantidadTexto(evento.target.value)}
                  />

                  <Input
                    label="Fecha de entrada"
                    type="date"
                    value={fecha}
                    onChange={(evento) => setFecha(evento.target.value)}
                  />

                  <Input
                    label="Razón"
                    value={razon}
                    onChange={(evento) => setRazon(evento.target.value)}
                  />
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-ink-500">
                    {seleccionada
                      ? `Pendiente de ${seleccionada.producto}: ${cantidad(seleccionada.pendiente)} (comprado ${cantidad(
                          seleccionada.cantidad_comprada,
                        )}, ingresado ${cantidad(seleccionada.cantidad_ingresada)}).`
                      : 'Elige el producto a ingresar.'}
                  </p>
                  <Button
                    size="sm"
                    loading={registrar.isPending}
                    leftIcon={<PackagePlus className="size-4" />}
                    onClick={enviar}
                  >
                    Registrar entrada
                  </Button>
                </div>
              </section>
            ) : (
              <Alert variant="success" title="Todo ingresado">
                <span className={ICONO_EN_ALERTA}>
                  <PackageCheck className="size-4" aria-hidden="true" />
                  No queda nada pendiente por ingresar de esta compra.
                </span>
              </Alert>
            )}

            {/* Líneas de la compra */}
            <section className="rounded-card border border-ink-100 bg-white">
              <h3 className="border-b border-ink-100 px-4 py-3 text-sm font-semibold text-ink-800">
                Líneas de la compra
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[13px]">
                  <thead>
                    <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="px-3 py-2">Producto</th>
                      <th className="px-3 py-2 text-right">Comprado</th>
                      <th className="px-3 py-2 text-right">Ingresado</th>
                      <th className="px-3 py-2 text-right">Pendiente</th>
                      <th className="px-3 py-2 text-right">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productos.map((linea) => (
                      <tr key={linea.producto_id} className="border-t border-ink-100">
                        <td className="px-3 py-2 font-medium text-ink-800">{linea.producto}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-ink-700">
                          {cantidad(linea.cantidad_comprada)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-ink-700">
                          {cantidad(linea.cantidad_ingresada)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-ink-900">
                          {cantidad(linea.pendiente)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {linea.pendiente <= 0 ? (
                            <Badge variant="success">COMPLETO</Badge>
                          ) : linea.cantidad_ingresada > 0 ? (
                            <Badge variant="warning">PARCIAL</Badge>
                          ) : (
                            <Badge variant="neutral">SIN INGRESAR</Badge>
                          )}
                        </td>
                      </tr>
                    ))}

                    {productos.length === 0 ? (
                      <tr className="border-t border-ink-100">
                        <td colSpan={5} className="px-3 py-6 text-center text-sm text-ink-500">
                          La compra no tiene líneas.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Entradas registradas */}
            <section className="rounded-card border border-ink-100 bg-white">
              <h3 className="border-b border-ink-100 px-4 py-3 text-sm font-semibold text-ink-800">
                Entradas registradas
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[13px]">
                  <thead>
                    <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="px-3 py-2">Fecha</th>
                      <th className="px-3 py-2">Producto</th>
                      <th className="px-3 py-2">Razón</th>
                      <th className="px-3 py-2 text-right">Cantidad</th>
                      <th className="px-3 py-2 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entradas.map((entrada) => (
                      <tr key={entrada.detalle_id} className="border-t border-ink-100">
                        <td className="px-3 py-2 whitespace-nowrap text-ink-700">
                          {fechaCorta(entrada.fecha_entrada)}
                        </td>
                        <td className="px-3 py-2 font-medium text-ink-800">{entrada.producto}</td>
                        <td className="px-3 py-2 text-xs text-ink-500">{entrada.descripcion ?? '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-ink-700">
                          {cantidad(entrada.cantidad)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Anular la entrada"
                            aria-label={`Anular la entrada de ${entrada.producto}`}
                            onClick={() => setPorAnular(entrada)}
                          >
                            <Trash2 className="size-4 text-rose-600" />
                          </Button>
                        </td>
                      </tr>
                    ))}

                    {entradas.length === 0 ? (
                      <tr className="border-t border-ink-100">
                        <td colSpan={5} className="px-3 py-6 text-center text-sm text-ink-500">
                          Todavía no hay entradas registradas.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        ) : null}
      </Dialog>

      <ConfirmDialog
        open={porAnular !== null}
        title={`¿Anular la entrada de ${porAnular?.producto ?? ''}?`}
        description="Se quita del stock y la línea vuelve a quedar por almacenar si ya no tiene más entradas."
        confirmLabel="Anular entrada"
        loading={anular.isPending}
        onConfirm={quitar}
        onClose={() => setPorAnular(null)}
      />
    </>
  );
}
